import { Sidc } from '@/common/MilSymbol/Sidc';
import MilSymbolDTO from '@/models/MilSymbol';

export interface MilSymbolTextPromptMatchDTO {
    score: number;
    sidc: string | Sidc;
    tokensMatched: string[];
    tokensUnmatchedTextPrompt: string[];
    tokensUnmatchedMilSymbol: string[];
}

function tokensToString(tokens: string[]): string {
    return tokens.map((t) => t.replace('_', ' ')).join(' ');
}

export class MilSymbolTextPromptMatch implements MilSymbolTextPromptMatchDTO {
    readonly score: number;
    readonly sidc: Sidc;
    // These are the tokens from the text prompt (after expansions and extensions)
    // that were matched against tokens from the descriptions of the parts of the military icon.
    readonly tokensMatched: string[];
    // These are the tokens from the text prompt (after expansions and extensions)
    // that were NOT matched against tokens from the descriptions of the parts of the military icon.
    readonly tokensUnmatchedTextPrompt: string[];
    // These are the tokens from the descriptions of the parts of the military icon that were
    // not matched by the text prompt.
    // For example, if part of the icon matched has description "tank 1st platoon", and the matched part
    // from the text prompt is "tank", then ["1st", "platoon"] will be part of this array.
    readonly tokensUnmatchedMilSymbol: string[];

    constructor(data: MilSymbolTextPromptMatchDTO) {
        this.score = data.score;
        this.sidc = new Sidc(data.sidc);
        this.tokensMatched = data.tokensMatched;
        this.tokensUnmatchedTextPrompt = data.tokensUnmatchedTextPrompt;
        this.tokensUnmatchedMilSymbol = data.tokensUnmatchedMilSymbol;
    }

    /**
     * This is one option for what we might want to display
     * in the v-autocomplete after the user makes a selection.
     */
    getTokensMilSymbolAsString(): string {
        const tokensMilSymbol = [...this.tokensMatched, ...this.tokensUnmatchedMilSymbol];
        return tokensToString(tokensMilSymbol);
    }

    getMilSymbolDTO(): MilSymbolDTO {
        return {
            code: this.sidc.underlying,
            specialHeadquarters: this.sidc.isNamedHeadquarters()
                ? this.getNameForNamedHeadquarters()
                : '',
        };
    }

    getNameForNamedHeadquarters(): string {
        // Return the first unmatched token that is less than 6 characters long.
        // I assume there generally won't be more than one, so this will be good enough.
        for (const tokenNotMatched of this.tokensUnmatchedTextPrompt) {
            if (tokenNotMatched.length <= 6) {
                return tokenNotMatched.toUpperCase();
            }
        }
        return '';
    }

    isACandidateForBeingANamedHeadquarters(): boolean {
        return (
            this.tokensUnmatchedTextPrompt.length > 0 &&
            this.sidc.hasNoMainIcon() &&
            this.sidc.isLandUnit()
        );
    }

    convertedToNamedHeadquarters(): MilSymbolTextPromptMatch {
        if (!this.isACandidateForBeingANamedHeadquarters()) {
            throw new Error(`Cannot convert to named headquarters\n${this}`);
        }
        const newSidc = this.sidc.convertedToNamedHeadquarters();
        return new MilSymbolTextPromptMatch({
            score: this.score,
            sidc: newSidc,
            tokensMatched: this.tokensMatched,
            tokensUnmatchedTextPrompt: this.tokensUnmatchedTextPrompt,
            tokensUnmatchedMilSymbol: this.tokensUnmatchedMilSymbol,
        });
    }
}
